import { Loading } from '@/core/ui/components/Loading';

import { getErrorMessage, useSelectedUnit, useServices, useToast } from '@/app/providers';
import { ApiError } from '@yawi/infrastructure';
import { resolveAmenityPricing } from '@yawi/domain';
import { AmenityPricingMode } from '@yawi/types';
import { AmenityBookingModal } from '@/presentation/components/organisms/AmenityBookingModal';
import { MyReservationList } from '@/presentation/components/organisms/MyReservationList';
import { useI18n } from '@/shared/i18n';
import { Amenity } from '@/shared/types';
import { Calendar, DollarSign, ImageOff, Info, Users, Clock } from 'lucide-react';
import { useEffect, useState } from 'react';

const AmenityImage = ({ src, alt }: { src: string; alt: string }) => {
  const [hasError, setHasError] = useState(false);

  if (!src || hasError) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center gap-2 bg-slate-100 text-slate-400">
        <ImageOff size={32} strokeWidth={1.5} />
        <span className="text-xs font-medium">Imagen no disponible</span>
      </div>
    );
  }

  return (
    <img
      src={src ?? ''}
      alt={alt}
      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
      onError={() => setHasError(true)}
    />
  );
};

export const Amenities = () => {
  const { t } = useI18n();
  const toast = useToast();
  const { amenityRepo, amenityReservationRepo, userRepo, currentUser } = useServices();
  const { selectedUnit, selectedUnitId, hasMultipleUnits } = useSelectedUnit();

  const [amenities, setAmenities] = useState<Amenity[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedAmenity, setSelectedAmenity] = useState<Amenity | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string>('');
  const [currentUserName, setCurrentUserName] = useState<string>('');
  // Force refresh of reservations list
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [amenitiesData, user] = await Promise.all([
          amenityRepo.getAmenities({ activeOnly: true }).then((r) => r.data),
          userRepo.getCurrentUser()
        ]);
        setAmenities(amenitiesData);
        setCurrentUserId(user.id);
        setCurrentUserName(user.name);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [amenityRepo, userRepo]);

  const handleBookingConfirm = async (bookingData: { amenityId: string; date: string; startTime: string; hours: number }) => {
    try {
      await amenityReservationRepo.createReservation({
        ...bookingData,
        unitId: hasMultipleUnits && selectedUnitId ? selectedUnitId : undefined,
      });

      toast.success(t('amenities.success'));
      setSelectedAmenity(null);
      setRefreshTrigger(prev => prev + 1);
    } catch (e) {
      console.error(e);
      if (ApiError.isApiError(e) && e.code === 'AMENITY_6006') {
        toast.error(getErrorMessage(e), t('amenities.errors.title'));
      } else if (ApiError.isApiError(e) && e.statusCode === 409) {
        toast.error(t('amenities.errors.slotTaken'), t('amenities.errors.title'));
      } else {
        toast.error(getErrorMessage(e), t('amenities.errors.title'));
      }
    }
  };

  // Con varias unidades (en secciones distintas) se muestran las amenidades de
  // la unidad elegida: sin secciones = todas (§27). Con una sola ya filtra el backend.
  const visibleAmenities = hasMultipleUnits
    ? amenities.filter(
        (amenity) =>
          !amenity.sectionIds?.length ||
          (!!selectedUnit?.sectionId && amenity.sectionIds.includes(selectedUnit.sectionId)),
      )
    : amenities;

  if (loading) return <Loading />;

  return (
    <div className="space-y-8">
      <div>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-slate-800">{t('amenities.title')}</h2>
          <div className="text-sm text-slate-500">
            {t('amenities.balance')}: <span className="font-bold text-emerald-600">$0.00</span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {visibleAmenities.map((amenity) => {
            const pricing = resolveAmenityPricing(amenity, selectedUnit?.sectionId);
            return (
            <div key={amenity.id} className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden group hover:shadow-md transition-shadow">
              <div className="h-48 overflow-hidden relative">
                <AmenityImage src={amenity.imageUrl ?? ''} alt={amenity.name} />
                <div className="absolute top-3 right-3 bg-white/90 backdrop-blur text-slate-800 text-xs font-bold px-2 py-1 rounded shadow-sm flex items-center gap-1">
                  <Users size={12} />
                  {t('amenities.capacity')}: {amenity.capacity}
                </div>
                {amenity.requiresApproval && (
                  <div className="absolute top-3 left-3 bg-amber-500 text-white text-[10px] font-bold px-2 py-1 rounded shadow-sm flex items-center gap-1">
                    <Clock size={10} />
                    Requiere aprobación
                  </div>
                )}
              </div>
              
              <div className="p-5">
                <h3 className="font-bold text-lg text-slate-800 mb-2">{amenity.name}</h3>
                
                <div className="space-y-2 mb-6">
                  <div className="flex items-center text-sm text-slate-600">
                    <DollarSign size={16} className="text-slate-400 mr-2" />
                    {pricing.price === 0
                      ? t('amenities.free')
                      : `${pricing.price}${pricing.pricingMode === AmenityPricingMode.FLAT ? ' por reserva' : '/hr'}`}
                  </div>
                  <div className="flex items-center text-sm text-slate-600">
                    <Info size={16} className="text-slate-400 mr-2" />
                    {pricing.depositAmount > 0 ? t('amenities.deposit_req') : t('amenities.no_deposit')}
                  </div>
                </div>

                <button 
                  onClick={() => setSelectedAmenity(amenity)}
                  className="w-full bg-slate-900 hover:bg-primary-600 text-white py-2.5 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                >
                  <Calendar size={16} />
                  {t('amenities.book_btn')}
                </button>
              </div>
            </div>
            );
          })}
        </div>
      </div>

      {/* User Reservations */}
      <div key={refreshTrigger}>
        <MyReservationList userId={currentUserId} units={currentUser?.units} />
      </div>

      {selectedAmenity && (
        <AmenityBookingModal
          amenity={selectedAmenity}
          memberId={currentUserId}
          memberName={currentUserName}
          sectionId={selectedUnit?.sectionId}
          onClose={() => setSelectedAmenity(null)}
          onConfirm={handleBookingConfirm}
        />
      )}
    </div>
  );
};